import type { FilterError } from "./ast";

/** One character of a text token; `esc` when it was written with a backslash (`\&`, `\*`). */
export interface Char {
  c: string;
  esc: boolean;
}

export type Token =
  | { type: "and" | "or" | "not" | "lparen" | "rparen" | "comma"; start: number; end: number }
  | { type: "text"; chars: Char[]; start: number; end: number; source: string };

const OPERATORS: Record<string, Exclude<Token["type"], "text" | "not">> = {
  "&": "and",
  "|": "or",
  "(": "lparen",
  ")": "rparen",
  ",": "comma",
};

/**
 * Full-width ASCII → ASCII (＆ → &, （ → (, ， → ,, ： → :) and ideographic space → space.
 * Length-preserving, so positions still point into the original query.
 */
export function foldWidth(s: string): string {
  return s.replace(/[！-～]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0)).replace(/　/g, " ");
}

const isSpace = (c: string) => /\s/.test(c);

export class TokenizeError extends Error {
  constructor(readonly error: FilterError) {
    super(error.message);
  }
}

/**
 * Splits a query into operators and text tokens. Text runs until an unescaped `&`, `|`, `(`,
 * `)` or `,`; `!` is NOT only at the start of a term (so `#Hello!` keeps its `!`). A backslash
 * makes the next character literal. Text tokens are trimmed; whitespace-only text is dropped.
 */
export function tokenize(query: string): Token[] {
  const s = foldWidth(query);
  const tokens: Token[] = [];
  let chars: Char[] = [];
  let textStart = -1;

  const flush = () => {
    if (chars.length > 0) {
      // Trim unescaped whitespace on both sides.
      let a = 0;
      let b = chars.length;
      while (a < b && !chars[a]?.esc && isSpace(chars[a]?.c ?? "")) a++;
      while (b > a && !chars[b - 1]?.esc && isSpace(chars[b - 1]?.c ?? "")) b--;
      if (b > a) {
        // Map trimmed char indices back to source offsets (escapes take two source chars).
        const offsets: number[] = [];
        let pos = textStart;
        for (const ch of chars) {
          if (ch.esc) pos++;
          offsets.push(pos);
          pos++;
        }
        const start = (offsets[a] ?? textStart) - (chars[a]?.esc ? 1 : 0);
        const end = (offsets[b - 1] ?? textStart) + 1;
        tokens.push({ type: "text", chars: chars.slice(a, b), start, end, source: query.slice(start, end) });
      }
    }
    chars = [];
    textStart = -1;
  };

  for (let i = 0; i < s.length; i++) {
    const c = s[i] as string;
    if (c === "\\") {
      const next = s[i + 1];
      if (next === undefined) {
        throw new TokenizeError({
          code: "DANGLING_ESCAPE",
          message: "A backslash must be followed by the character it escapes",
          start: i,
          end: i + 1,
        });
      }
      if (textStart < 0) textStart = i;
      chars.push({ c: next, esc: true });
      i++;
      continue;
    }
    const op = OPERATORS[c];
    if (op) {
      flush();
      tokens.push({ type: op, start: i, end: i + 1 });
      continue;
    }
    if (c === "!" && chars.every((ch) => !ch.esc && isSpace(ch.c))) {
      chars = [];
      textStart = -1;
      tokens.push({ type: "not", start: i, end: i + 1 });
      continue;
    }
    if (textStart < 0) textStart = i;
    chars.push({ c, esc: false });
  }
  flush();
  return tokens;
}
